/**
 * Pure formatting and sorting utilities for client portal billing data.
 * No FHIR dependencies — safe to import from any layer.
 */
import {
  coverageOrderToPriority as fhirCoverageOrderToPriority,
  priorityToCoverageOrder as fhirPriorityToCoverageOrder,
} from '@unify/fhir-model/resources/coverage';
import type {
  ClientPortalBillingDocument,
  ClientPortalBillingDocumentKind,
  ClientPortalBillingServiceLine,
  ClientPortalBillingStatus,
  ClientPortalCoverageSummary,
  ClientPortalInsurancePaymentStatus,
  ClientPortalInsurancePriority,
  ClientPortalInvoiceSummary,
  ClientPortalPaymentChargeType,
  ClientPortalPaymentSummary,
} from './clientPortalFhirTypes';

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/** Formats a cents integer as a USD string (e.g. 4500 → "$45.00"). */
export function formatMoney(cents?: number): string {
  if (cents == null || !Number.isFinite(cents)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** Formats an ISO date string for display. Returns "—" for missing/invalid. */
export function formatBillingDate(iso?: string): string {
  if (!iso?.trim()) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

/** Formats a service period as "Jan 1 – Jan 31, 2026" or single date if start === end. */
export function formatServicePeriod(start?: string, end?: string): string {
  const s = start?.trim();
  const e = end?.trim();
  if (!s && !e) return '—';
  if (!s) return formatBillingDate(e);
  if (!e || s === e) return formatBillingDate(s);

  const ds = new Date(s);
  const de = new Date(e);
  if (Number.isNaN(ds.getTime()) || Number.isNaN(de.getTime())) return formatBillingDate(s);

  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
  const fmtYear = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const sameYear = ds.getFullYear() === de.getFullYear();
  return sameYear
    ? `${fmt.format(ds)} – ${fmtYear.format(de)}`
    : `${fmtYear.format(ds)} – ${fmtYear.format(de)}`;
}

// ---------------------------------------------------------------------------
// Masking
// ---------------------------------------------------------------------------

/** Masks a member ID to show only the last 4 characters (e.g. "***1234"). */
export function maskMemberId(value?: string): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (trimmed.length <= 4) return `***${trimmed}`;
  return `***${trimmed.slice(-4)}`;
}

/** Masks a group number to show only the last 4 characters. */
export function maskGroupNumber(value?: string): string | undefined {
  return maskMemberId(value);
}

// ---------------------------------------------------------------------------
// Invoice calculations
// ---------------------------------------------------------------------------

/** Returns the amount due: max(0, clientResponsibility - clientPaid). */
export function calculateInvoiceAmountDue(
  invoice: Pick<ClientPortalInvoiceSummary, 'clientResponsibilityCents' | 'clientPaidCents'>,
): number {
  return Math.max(0, invoice.clientResponsibilityCents - invoice.clientPaidCents);
}

/** Returns the client balance due for a service line. */
export function calculateServiceLineAmountDue(
  line: Pick<ClientPortalBillingServiceLine, 'clientResponsibilityCents' | 'clientPaidCents'>,
): number {
  if (line.clientResponsibilityCents == null) return 0;
  return Math.max(0, line.clientResponsibilityCents - (line.clientPaidCents ?? 0));
}

/** Uses explicit amountDueCents when present; otherwise derives from responsibility/paid. */
export function resolveServiceLineAmountDue(
  line: Pick<
    ClientPortalBillingServiceLine,
    'amountDueCents' | 'clientResponsibilityCents' | 'clientPaidCents'
  >,
): number {
  if (line.amountDueCents != null) return line.amountDueCents;
  return calculateServiceLineAmountDue(line);
}

// ---------------------------------------------------------------------------
// Status labels and colors
// ---------------------------------------------------------------------------

const BILLING_STATUS_LABEL: Record<ClientPortalBillingStatus, string> = {
  due: 'Balance due',
  overdue: 'Overdue',
  paid: 'Paid',
  'partially-paid': 'Partially paid',
  processing: 'Processing',
  void: 'Void',
  refunded: 'Refunded',
  'insurance-pending': 'Insurance pending',
  unknown: 'Unknown',
};

export function billingStatusLabel(status: ClientPortalBillingStatus): string {
  return BILLING_STATUS_LABEL[status] ?? 'Unknown';
}

const BILLING_STATUS_COLOR: Record<ClientPortalBillingStatus, string> = {
  due: 'orange',
  overdue: 'red',
  paid: 'green',
  'partially-paid': 'yellow',
  processing: 'blue',
  void: 'gray',
  refunded: 'teal',
  'insurance-pending': 'violet',
  unknown: 'gray',
};

export function billingStatusColor(status: ClientPortalBillingStatus): string {
  return BILLING_STATUS_COLOR[status] ?? 'gray';
}

const PAYMENT_STATUS_LABEL: Record<ClientPortalPaymentSummary['status'], string> = {
  succeeded: 'Paid',
  processing: 'Processing',
  scheduled: 'Scheduled',
  failed: 'Failed',
  refunded: 'Refunded',
  unknown: 'Unknown',
};

export function paymentStatusLabel(status: ClientPortalPaymentSummary['status']): string {
  return PAYMENT_STATUS_LABEL[status] ?? 'Unknown';
}

export function paymentStatusColor(status: ClientPortalPaymentSummary['status']): string {
  switch (status) {
    case 'succeeded': return 'green';
    case 'processing': return 'blue';
    case 'scheduled': return 'violet';
    case 'failed': return 'red';
    case 'refunded': return 'teal';
    default: return 'gray';
  }
}

const PAYMENT_CHARGE_TYPE_LABEL: Record<ClientPortalPaymentChargeType, string> = {
  'auto-charge': 'Auto-charge',
  manual: 'Manual payment',
  unknown: '—',
};

export function paymentChargeTypeLabel(chargeType: ClientPortalPaymentChargeType): string {
  return PAYMENT_CHARGE_TYPE_LABEL[chargeType] ?? '—';
}

// ---------------------------------------------------------------------------
// Document kind labels
// ---------------------------------------------------------------------------

const DOCUMENT_KIND_LABEL: Record<ClientPortalBillingDocumentKind, string> = {
  statement: 'Statement',
  superbill: 'Superbill',
  eob: 'Explanation of Benefits',
  gfe: 'Good Faith Estimate',
  receipt: 'Receipt',
  invoice: 'Invoice',
  other: 'Document',
};

export function documentKindLabel(kind: ClientPortalBillingDocumentKind): string {
  return DOCUMENT_KIND_LABEL[kind] ?? 'Document';
}

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

/** Sorts invoices: open/due first (newest due date), then paid (newest date). */
export function sortInvoices(invoices: ClientPortalInvoiceSummary[]): ClientPortalInvoiceSummary[] {
  const openStatuses: ClientPortalBillingStatus[] = ['due', 'overdue', 'partially-paid', 'processing', 'insurance-pending'];
  return [...invoices].sort((a, b) => {
    const aOpen = openStatuses.includes(a.status);
    const bOpen = openStatuses.includes(b.status);
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    const da = new Date(a.dueDate ?? a.date ?? '').getTime();
    const db = new Date(b.dueDate ?? b.date ?? '').getTime();
    const sa = Number.isNaN(da) ? 0 : da;
    const sb = Number.isNaN(db) ? 0 : db;
    return sb - sa;
  });
}

/** Sorts service lines newest-first by date of service. */
export function sortServiceLines(
  lines: ClientPortalBillingServiceLine[],
): ClientPortalBillingServiceLine[] {
  return [...lines].sort((a, b) => {
    const da = new Date(a.dateOfService).getTime();
    const db = new Date(b.dateOfService).getTime();
    const sa = Number.isNaN(da) ? 0 : da;
    const sb = Number.isNaN(db) ? 0 : db;
    return sb - sa;
  });
}

/** Heuristic for assessment service labels when encounterKind is absent. */
export function looksLikeAssessmentServiceType(serviceType: string): boolean {
  const t = serviceType.trim().toLowerCase();
  if (!t) return false;
  if (t.includes('assessment')) return true;
  if (/\bphq-?9\b|\bgad-?7\b|\bphq-?a\b|\bscreening\b|\bquestionnaire\b/.test(t)) return true;
  return false;
}

/** Resolves appointment vs assessment for filtering; fills gaps when encounterKind was not set. */
export function resolveServiceLineEncounterKind(
  line: ClientPortalBillingServiceLine,
): 'appointment' | 'assessment' {
  if (line.encounterKind === 'appointment' || line.encounterKind === 'assessment') {
    return line.encounterKind;
  }
  if (looksLikeAssessmentServiceType(line.serviceType)) return 'assessment';
  return 'appointment';
}

/** Client-facing label: "Appointment – Individual therapy" or "Assessment – Couples therapy". */
export function formatServiceLineLabel(line: ClientPortalBillingServiceLine): string {
  const kind = resolveServiceLineEncounterKind(line) === 'assessment' ? 'Assessment' : 'Appointment';
  const eocLabel = line.episodeOfCareLabel?.trim() || line.serviceType.trim() || 'Care episode';
  return `${kind} – ${eocLabel}`;
}

const INSURANCE_PAYMENT_STATUS_LABEL: Record<ClientPortalInsurancePaymentStatus, string> = {
  paid: 'Paid',
  pending: 'Pending',
  processing: 'Processing',
  denied: 'Denied',
  partial: 'Partially paid',
  unknown: 'Unknown',
};

export function insurancePaymentStatusLabel(status: ClientPortalInsurancePaymentStatus): string {
  return INSURANCE_PAYMENT_STATUS_LABEL[status] ?? 'Unknown';
}

export function insurancePaymentStatusColor(status: ClientPortalInsurancePaymentStatus): string {
  switch (status) {
    case 'paid': return 'green';
    case 'pending': return 'violet';
    case 'processing': return 'blue';
    case 'denied': return 'red';
    case 'partial': return 'yellow';
    default: return 'gray';
  }
}

/** Ensures every service line has encounterKind for filtering and badges. */
export function withResolvedServiceLineKinds(
  lines: ClientPortalBillingServiceLine[],
): ClientPortalBillingServiceLine[] {
  return lines.map((line) => ({
    ...line,
    encounterKind: resolveServiceLineEncounterKind(line),
  }));
}

// ---------------------------------------------------------------------------
// Document grouping
// ---------------------------------------------------------------------------

/** Groups a flat document list into per-kind arrays. */
export function groupDocumentsByKind(
  docs: ClientPortalBillingDocument[],
): Record<ClientPortalBillingDocumentKind, ClientPortalBillingDocument[]> {
  const result: Record<ClientPortalBillingDocumentKind, ClientPortalBillingDocument[]> = {
    statement: [],
    superbill: [],
    eob: [],
    gfe: [],
    receipt: [],
    invoice: [],
    other: [],
  };
  for (const doc of docs) {
    result[doc.kind].push(doc);
  }
  return result;
}

// ---------------------------------------------------------------------------
// Insurance priority slots (primary / secondary / tertiary)
// ---------------------------------------------------------------------------

export const CLIENT_PORTAL_INSURANCE_PRIORITIES: readonly ClientPortalInsurancePriority[] = [
  'primary',
  'secondary',
  'tertiary',
] as const;

const INSURANCE_PRIORITY_LABEL: Record<ClientPortalInsurancePriority, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  tertiary: 'Tertiary',
};

const INSURANCE_PRIORITY_DESCRIPTION: Record<ClientPortalInsurancePriority, string> = {
  primary: 'Your main insurance — billed first for eligible services.',
  secondary: 'Optional second plan — may cover costs after primary.',
  tertiary: 'Optional third plan — may cover remaining costs after primary and secondary.',
};

export function insurancePriorityLabel(priority: ClientPortalInsurancePriority): string {
  return INSURANCE_PRIORITY_LABEL[priority];
}

export function insurancePriorityDescription(priority: ClientPortalInsurancePriority): string {
  return INSURANCE_PRIORITY_DESCRIPTION[priority];
}

/**
 * Plans must be added in order (primary, then secondary, then tertiary). Returns a
 * client-facing reason when earlier slots are still empty, or undefined when unlocked.
 */
export function insurancePriorityLockReason(
  priority: ClientPortalInsurancePriority,
  coverageByPriority: Record<ClientPortalInsurancePriority, ClientPortalCoverageSummary | undefined>,
): string | undefined {
  const missing = CLIENT_PORTAL_INSURANCE_PRIORITIES.slice(
    0,
    CLIENT_PORTAL_INSURANCE_PRIORITIES.indexOf(priority),
  ).filter((earlier) => coverageByPriority[earlier] == null);

  if (missing.length === 0) {
    return undefined;
  }

  const names = missing.map((p) => insurancePriorityLabel(p).toLowerCase()).join(' and ');
  return `Add your ${names} plan${missing.length > 1 ? 's' : ''} first`;
}

/** USPS 2-letter state from the patient's home address, else the first address. */
export function patientHomeState(patient?: {
  address?: Array<{ use?: string; state?: string }>;
}): string | undefined {
  const home = patient?.address?.find((a) => a.use === 'home') ?? patient?.address?.[0];
  const state = home?.state?.trim().toUpperCase();
  return state || undefined;
}

/**
 * When the client's state is known, only that state's payers belong in the picker.
 * If we do not know the client's state, keep the full directory.
 */
export function payerMatchesClientState(
  payerState: string | undefined,
  clientState: string | undefined,
): boolean {
  if (!clientState) {
    return true;
  }
  return payerState?.trim().toUpperCase() === clientState;
}

export function insurancePriorityBadgeColor(priority: ClientPortalInsurancePriority): string {
  if (priority === 'primary') return 'gold';
  if (priority === 'secondary') return 'blue';
  return 'gray';
}

/** Maps FHIR Coverage.order (1-based) to portal priority. */
export function coverageOrderToPriority(order?: number): ClientPortalInsurancePriority {
  return fhirCoverageOrderToPriority(order);
}

/** Maps portal priority to FHIR Coverage.order. */
export function priorityToCoverageOrder(priority: ClientPortalInsurancePriority): 1 | 2 | 3 {
  return fhirPriorityToCoverageOrder(priority);
}

/** Places each summary into its priority slot; unlabeled entries fill the next open slot. */
export function organizeCoverageByPriority(
  summaries: ClientPortalCoverageSummary[],
): Record<ClientPortalInsurancePriority, ClientPortalCoverageSummary | undefined> {
  const slots: Record<ClientPortalInsurancePriority, ClientPortalCoverageSummary | undefined> = {
    primary: undefined,
    secondary: undefined,
    tertiary: undefined,
  };
  const unassigned: ClientPortalCoverageSummary[] = [];

  for (const summary of summaries) {
    if (summary.priority && !slots[summary.priority]) {
      slots[summary.priority] = summary;
    } else if (!summary.priority) {
      unassigned.push(summary);
    }
  }

  for (const summary of unassigned) {
    const openPriority = CLIENT_PORTAL_INSURANCE_PRIORITIES.find((p) => !slots[p]);
    if (openPriority) {
      slots[openPriority] = { ...summary, priority: openPriority };
    }
  }

  return slots;
}
